<div class="rg-page-header">
    <div>
        <a routerLink="/organizations" class="text-decoration-none small">
            <i class="bi bi-arrow-left me-1"></i>
            Back to Organizations
        </a>

        <h3 class="fw-bold mb-1 mt-2">
            {{ organization?.name || 'Organization Details' }}
        </h3>

        <p class="text-muted mb-0">
            Manage users, licences, API credentials and reminders.
        </p>
    </div>

    <span class="badge" *ngIf="organization" [ngClass]="getStatusClass(organization.status)">
        {{ getStatusLabel(organization.status) }}
    </span>
</div>

<div class="container-fluid p-4">
    <div *ngIf="loading" class="text-muted">
        Loading organization...
    </div>

    <ng-container *ngIf="!loading && organization">
        <div class="card border-0 shadow-sm rg-card mb-4">
            <div class="card-body">
                <div class="row g-3">
                    <div class="col-md-4">
                        <small class="text-muted">Organization Name</small>
                        <div class="fw-semibold">{{ organization.name }}</div>
                    </div>

                    <div class="col-md-4">
                        <small class="text-muted">Contact Email</small>
                        <div class="fw-semibold">{{ organization.contactEmail || '—' }}</div>
                    </div>

                    <div class="col-md-4">
                        <small class="text-muted">Contact Phone</small>
                        <div class="fw-semibold">{{ organization.contactPhone || '—' }}</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm rg-card">
            <div class="card-header bg-white">
                <ul class="nav nav-pills gap-2">
                    <li class="nav-item">
                        <button class="nav-link" [class.active]="activeTab === 'overview'" (click)="setTab('overview')">
                            Overview
                        </button>
                    </li>

                    <li class="nav-item">
                        <button class="nav-link" [class.active]="activeTab === 'users'" (click)="setTab('users')">
                            Users
                        </button>
                    </li>

                    <li class="nav-item">
                        <button class="nav-link" [class.active]="activeTab === 'licences'" (click)="setTab('licences')">
                            Licences
                        </button>
                    </li>

                    <li class="nav-item">
                        <button class="nav-link" [class.active]="activeTab === 'credentials'"
                            (click)="setTab('credentials')">
                            API Credentials
                        </button>
                    </li>

                    <li class="nav-item">
                        <button class="nav-link" [class.active]="activeTab === 'reminders'"
                            (click)="setTab('reminders')">
                            Reminder Settings
                        </button>
                    </li>
                </ul>
            </div>

            <div class="card-body">
                <div *ngIf="activeTab === 'overview'">
                    <h5 class="fw-bold">Overview</h5>
                    <p class="text-muted mb-0">
                        This section will show organization-level summary, licence status,
                        latest validation activity and quick actions.
                    </p>
                </div>

                <div *ngIf="activeTab === 'users'">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 class="fw-bold mb-1">Organization Users</h5>
                            <p class="text-muted mb-0">
                                Manage organization contacts and licence reminder recipients.
                            </p>
                        </div>

                        <button class="btn btn-primary" data-bs-toggle="modal"
                            data-bs-target="#createOrganizationUserModal">
                            <i class="bi bi-plus-lg me-1"></i>
                            Add User
                        </button>
                    </div>

                    <div class="row g-2 align-items-center mb-3">
                        <div class="col-12 col-md-5">
                            <input type="text" class="form-control" placeholder="Search name or email..."
                                [(ngModel)]="usersSearch" (keyup.enter)="searchUsers()">
                        </div>

                        <div class="col-12 col-md-auto">
                            <button class="btn btn-outline-primary" (click)="searchUsers()">
                                <i class="bi bi-search me-1"></i>
                                Search
                            </button>
                        </div>

                        <div class="col-12 col-md-auto">
                            <button class="btn btn-outline-secondary" (click)="clearUsersSearch()">
                                Clear
                            </button>
                        </div>
                    </div>

                    <div class="table-responsive">
                        <table class="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Licence Emails</th>
                                    <th>Created</th>
                                    <th class="text-end">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr *ngIf="!usersLoading && users.length === 0">
                                    <td colspan="6" class="text-center py-4 text-muted">
                                        No record found.
                                    </td>
                                </tr>

                                <tr *ngFor="let user of users">
                                    <td>
                                        <div class="fw-semibold">{{ user.fullName }}</div>
                                        <small class="text-muted">{{ user.email }}</small>
                                    </td>

                                    <td>
                                        {{ getUserRoleLabel(user.role) }}
                                    </td>

                                    <td>
                                        <span class="badge" [ngClass]="getUserStatusClass(user.status)">
                                            {{ getUserStatusLabel(user.status) }}
                                        </span>
                                    </td>

                                    <td>
                                        <span *ngIf="user.receivesLicenceEmails" class="badge text-bg-primary">
                                            Enabled
                                        </span>
                                        <span *ngIf="!user.receivesLicenceEmails" class="badge text-bg-light">
                                            Disabled
                                        </span>
                                    </td>

                                    <td>
                                        {{ user.createdAt | date:'mediumDate' }}
                                    </td>

                                    <td class="text-end">
                                        <button class="btn btn-sm btn-outline-primary me-1"
                                            (click)="openEditUserModal(user)">
                                            Edit
                                        </button>

                                        <button class="btn btn-sm btn-outline-success me-1"
                                            (click)="changeUserStatus(user, 'activate')">
                                            Activate
                                        </button>

                                        <button class="btn btn-sm btn-outline-danger"
                                            (click)="changeUserStatus(user, 'disable')">
                                            Disable
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">
                            Page {{ usersMeta.page }} of {{ usersMeta.totalPages || 1 }}
                            — {{ usersMeta.totalItems }} item(s)
                        </small>

                        <div class="btn-group">
                            <button class="btn btn-outline-secondary btn-sm" [disabled]="usersPage <= 1 || usersLoading"
                                (click)="previousUsersPage()">
                                Previous
                            </button>

                            <button class="btn btn-outline-secondary btn-sm"
                                [disabled]="usersPage >= usersMeta.totalPages || usersLoading"
                                (click)="nextUsersPage()">
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div *ngIf="activeTab === 'licences'">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 class="fw-bold mb-1">Licences</h5>
                            <p class="text-muted mb-0">
                                Issue, renew, suspend and revoke organization licences.
                            </p>
                        </div>

                        <button class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#createLicenceModal">
                            <i class="bi bi-plus-lg me-1"></i>
                            Issue Licence
                        </button>
                    </div>

                    <div class="table-responsive">
                        <table class="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Licence Key</th>
                                    <th>Status</th>
                                    <th>Duration</th>
                                    <th>Starts</th>
                                    <th>Expires</th>
                                    <th class="text-end">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr *ngIf="!licencesLoading && licences.length === 0">
                                    <td colspan="6" class="text-center py-4 text-muted">
                                        No record found.
                                    </td>
                                </tr>

                                <tr *ngFor="let licence of licences">
                                    <td>
                                        <div class="fw-semibold">{{ licence.licenceKey }}</div>
                                        <small class="text-muted">{{ licence.id }}</small>
                                    </td>

                                    <td>
                                        <span class="badge" [ngClass]="getLicenceStatusClass(licence.status)">
                                            {{ getLicenceStatusLabel(licence.status) }}
                                        </span>
                                    </td>

                                    <td>
                                        <div>{{ licence.durationDays }} day(s)</div>
                                        <small class="text-muted">
                                            Grace: {{ licence.gracePeriodDays }} day(s)
                                        </small>
                                    </td>

                                    <td>{{ licence.startsAt | date:'mediumDate' }}</td>

                                    <td>{{ licence.expiresAt | date:'mediumDate' }}</td>

                                    <td class="text-end">
                                        <button class="btn btn-sm btn-outline-primary me-1"
                                            (click)="openRenewModal(licence)">
                                            Renew
                                        </button>

                                        <div class="btn-group">
                                            <button class="btn btn-sm btn-outline-dark dropdown-toggle"
                                                data-bs-toggle="dropdown">
                                                More
                                            </button>

                                            <ul class="dropdown-menu dropdown-menu-end">
                                                <li>
                                                    <button class="dropdown-item"
                                                        (click)="changeLicenceStatus(licence, 'suspend')">
                                                        Suspend
                                                    </button>
                                                </li>

                                                <li>
                                                    <button class="dropdown-item text-danger"
                                                        (click)="changeLicenceStatus(licence, 'revoke')">
                                                        Revoke
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">
                            Page {{ licencesMeta.page }} of {{ licencesMeta.totalPages || 1 }}
                            — {{ licencesMeta.totalItems }} item(s)
                        </small>

                        <div class="btn-group">
                            <button class="btn btn-outline-secondary btn-sm"
                                [disabled]="licencesPage <= 1 || licencesLoading" (click)="previousLicencesPage()">
                                Previous
                            </button>

                            <button class="btn btn-outline-secondary btn-sm"
                                [disabled]="licencesPage >= licencesMeta.totalPages || licencesLoading"
                                (click)="nextLicencesPage()">
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div *ngIf="activeTab === 'credentials'">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 class="fw-bold mb-1">API Credentials</h5>
                            <p class="text-muted mb-0">
                                Generate and revoke organization API access credentials.
                            </p>
                        </div>

                        <button class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#createCredentialModal">
                            <i class="bi bi-plus-lg me-1"></i>
                            Generate Credential
                        </button>
                    </div>

                    <div class="table-responsive">
                        <table class="table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>Client ID</th>
                                    <th>Status</th>
                                    <th>Last Used</th>
                                    <th>Created</th>
                                    <th class="text-end">Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr *ngIf="!credentialsLoading && credentials.length === 0">
                                    <td colspan="5" class="text-center py-4 text-muted">
                                        No record found.
                                    </td>
                                </tr>

                                <tr *ngFor="let credential of credentials">
                                    <td>
                                        <div class="fw-semibold">{{ credential.clientId }}</div>
                                        <small class="text-muted">{{ credential.id }}</small>
                                    </td>

                                    <td>
                                        <span class="badge" [ngClass]="getCredentialStatusClass(credential.status)">
                                            {{ getCredentialStatusLabel(credential.status) }}
                                        </span>
                                    </td>

                                    <td>
                                        {{ credential.lastUsedAt ? (credential.lastUsedAt | date:'medium') : 'Never' }}
                                    </td>

                                    <td>
                                        {{ credential.createdAt | date:'mediumDate' }}
                                    </td>

                                    <td class="text-end">
                                        <button class="btn btn-sm btn-outline-secondary me-1"
                                            (click)="copyText(credential.clientId)">
                                            Copy Client ID
                                        </button>

                                        <button class="btn btn-sm btn-outline-danger"
                                            [disabled]="credential.status !== 1" (click)="revokeCredential(credential)">
                                            Revoke
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="d-flex justify-content-between align-items-center mt-3">
                        <small class="text-muted">
                            Page {{ credentialsMeta.page }} of {{ credentialsMeta.totalPages || 1 }}
                            — {{ credentialsMeta.totalItems }} item(s)
                        </small>

                        <div class="btn-group">
                            <button class="btn btn-outline-secondary btn-sm"
                                [disabled]="credentialsPage <= 1 || credentialsLoading"
                                (click)="previousCredentialsPage()">
                                Previous
                            </button>

                            <button class="btn btn-outline-secondary btn-sm"
                                [disabled]="credentialsPage >= credentialsMeta.totalPages || credentialsLoading"
                                (click)="nextCredentialsPage()">
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div *ngIf="activeTab === 'reminders'">
                    <div class="mb-3">
                        <h5 class="fw-bold mb-1">Reminder Settings</h5>
                        <p class="text-muted mb-0">
                            Configure licence expiry reminder emails for this organization.
                        </p>
                    </div>

                    <div *ngIf="reminderLoading" class="text-muted">
                        Loading reminder settings...
                    </div>

                    <form *ngIf="!reminderLoading" (ngSubmit)="saveReminderSetting()" class="rg-reminder-form">
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" id="reminderEnabled" name="isEnabled"
                                [(ngModel)]="reminderModel.isEnabled">

                            <label class="form-check-label" for="reminderEnabled">
                                Enable licence expiry reminders
                            </label>
                        </div>

                        <div class="mb-3">
                            <label class="form-label">Reminder Days Before Expiry</label>
                            <input type="text" class="form-control" name="reminderDaysBeforeExpiry"
                                placeholder="30,14,7,3,1" [(ngModel)]="reminderModel.reminderDaysBeforeExpiry">

                            <small class="text-muted">
                                Enter comma-separated numbers. Example: 30,14,7,3,1
                            </small>
                        </div>

                        <button class="btn btn-primary" type="submit" [disabled]="savingReminder">
                            <span *ngIf="!savingReminder">Save Settings</span>
                            <span *ngIf="savingReminder">Saving...</span>
                        </button>
                    </form>
                </div>
            </div>
        </div>
    </ng-container>
</div>
<div class="modal fade" id="createOrganizationUserModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rg-modal">
            <div class="modal-header">
                <h5 class="modal-title fw-bold">Add Organization User</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <form (ngSubmit)="createOrganizationUser()">
                <div class="modal-body">
                    <div class="mb-3">
                        <label class="form-label">Full Name</label>
                        <input type="text" class="form-control" name="fullName" placeholder="John Doe"
                            [(ngModel)]="createUserModel.fullName">
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Email</label>
                        <input type="email" class="form-control" name="email" placeholder="john@company.com"
                            [(ngModel)]="createUserModel.email">
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Role</label>
                        <select class="form-select" name="role" [(ngModel)]="createUserModel.role">
                            <option [ngValue]="1">Owner</option>
                            <option [ngValue]="2">Admin</option>
                            <option [ngValue]="3">User</option>
                        </select>
                    </div>

                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="receivesLicenceEmails"
                            name="receivesLicenceEmails" [(ngModel)]="createUserModel.receivesLicenceEmails">

                        <label class="form-check-label" for="receivesLicenceEmails">
                            Receive licence expiry reminder emails
                        </label>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                        Cancel
                    </button>

                    <button type="submit" class="btn btn-primary" [disabled]="creatingUser">
                        <span *ngIf="!creatingUser">Add User</span>
                        <span *ngIf="creatingUser">Adding...</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
<div class="modal fade" id="editOrganizationUserModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rg-modal">
            <div class="modal-header">
                <h5 class="modal-title fw-bold">Edit Organization User</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <form (ngSubmit)="updateOrganizationUser()">
                <div class="modal-body">
                    <div class="mb-3">
                        <label class="form-label">Full Name</label>
                        <input type="text" class="form-control" name="editFullName"
                            [(ngModel)]="editUserModel.fullName">
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Email</label>
                        <input type="email" class="form-control" name="editEmail"
                            [(ngModel)]="editUserModel.email">
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Role</label>
                        <select class="form-select" name="editRole" [(ngModel)]="editUserModel.role">
                            <option [ngValue]="1">Owner</option>
                            <option [ngValue]="2">Admin</option>
                            <option [ngValue]="3">User</option>
                        </select>
                    </div>

                    <div class="form-check">
                        <input class="form-check-input" type="checkbox" id="editReceivesLicenceEmails"
                            name="editReceivesLicenceEmails" [(ngModel)]="editUserModel.receivesLicenceEmails">

                        <label class="form-check-label" for="editReceivesLicenceEmails">
                            Receive licence expiry reminder emails
                        </label>
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                        Cancel
                    </button>

                    <button type="submit" class="btn btn-primary" [disabled]="updatingUser">
                        <span *ngIf="!updatingUser">Save User</span>
                        <span *ngIf="updatingUser">Saving...</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
<div class="modal fade" id="createLicenceModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rg-modal">
            <div class="modal-header">
                <h5 class="modal-title fw-bold">Issue Licence</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <form (ngSubmit)="createLicence()">
                <div class="modal-body">
                    <div class="mb-3">
                        <label class="form-label">Duration Days</label>
                        <input type="number" class="form-control" name="durationDays"
                            [(ngModel)]="createLicenceModel.durationDays">
                    </div>

                    <div>
                        <label class="form-label">Grace Period Days</label>
                        <input type="number" class="form-control" name="gracePeriodDays"
                            [(ngModel)]="createLicenceModel.gracePeriodDays">
                    </div>
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                        Cancel
                    </button>

                    <button type="submit" class="btn btn-primary" [disabled]="creatingLicence">
                        <span *ngIf="!creatingLicence">Issue Licence</span>
                        <span *ngIf="creatingLicence">Issuing...</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
<div class="modal fade" id="renewLicenceModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rg-modal">
            <div class="modal-header">
                <h5 class="modal-title fw-bold">Renew Licence</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <form (ngSubmit)="renewLicence()">
                <div class="modal-body">
                    <div class="alert alert-light border" *ngIf="selectedLicence">
                        <small class="text-muted">Licence Key</small>
                        <div class="fw-semibold">{{ selectedLicence.licenceKey }}</div>
                    </div>

                    <label class="form-label">Additional Days</label>
                    <input type="number" class="form-control" name="additionalDays"
                        [(ngModel)]="renewModel.additionalDays">
                </div>

                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                        Cancel
                    </button>

                    <button type="submit" class="btn btn-primary" [disabled]="renewingLicence">
                        <span *ngIf="!renewingLicence">Renew Licence</span>
                        <span *ngIf="renewingLicence">Renewing...</span>
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>
<div class="modal fade" id="createCredentialModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content rg-modal">
            <div class="modal-header">
                <h5 class="modal-title fw-bold">Generate API Credential</h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>

            <div class="modal-body">
                <div class="alert alert-warning">
                    <strong>Important:</strong>
                    The client secret will only be shown once. Copy and store it securely.
                </div>

                <button class="btn btn-primary" [disabled]="creatingCredential" (click)="createCredential()">
                    <span *ngIf="!creatingCredential">
                        <i class="bi bi-key me-1"></i>
                        Generate Credential
                    </span>
                    <span *ngIf="creatingCredential">Generating...</span>
                </button>

                <div class="mt-4" *ngIf="newCredential">
                    <div class="mb-3">
                        <label class="form-label">Client ID</label>
                        <div class="input-group">
                            <input class="form-control" [value]="newCredential.clientId" readonly>
                            <button class="btn btn-outline-secondary" type="button"
                                (click)="copyText(newCredential.clientId)">
                                Copy
                            </button>
                        </div>
                    </div>

                    <div>
                        <label class="form-label">Client Secret</label>
                        <div class="input-group">
                            <input class="form-control" [value]="newCredential.clientSecret" readonly>
                            <button class="btn btn-outline-secondary" type="button"
                                (click)="copyText(newCredential.clientSecret)">
                                Copy
                            </button>
                        </div>
                        <small class="text-danger fw-semibold">
                            {{ newCredential.warning }}
                        </small>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
                    Close
                </button>
            </div>
        </div>
    </div>
</div>
